"use client";
import React, { useEffect, useState } from "react";
import { SidebarSectionCard } from "./cards/SidebarSectionCard";
import { useAppSelector } from "lib/redux/hooks";
import { selectScoreAnalysis } from "lib/redux/scoreAnalysisSlice";
import Image from "next/image";
import { PremiumCard } from "components/PremiumCard";
import { selectIsPremiumUser } from "lib/redux/uiSlice";
import Link from "next/link";

interface LeftSidebarCardsProps {
  handleAnalyseResume: () => void;
}

/**
 * Renders the 6 section cards .
 */
export const LeftSidebarCards: React.FC<LeftSidebarCardsProps> = ({
  handleAnalyseResume,
}) => {
  const scoreAnalysis = useAppSelector(selectScoreAnalysis);
  const isPremiumUser = useAppSelector(selectIsPremiumUser);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [isClient, setIsClient] = useState<boolean>(false);
  const [initialIsPremium, setInitialIsPremium] = useState<string | null>(null);

  useEffect(() => {
    // Mark as client-side rendered
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Wait for both client-side render and auth check
    if (isClient) {
      const token = localStorage.getItem("access-token");
      const storedPremium = localStorage.getItem("is-premium");
      setInitialIsPremium(storedPremium);
      // Add small delay to ensure Redux has hydrated (slightly longer to reduce flash)
      const timer = setTimeout(() => {
        setIsAuthLoading(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isClient]);

  // Return loading state if no scores yet
  if (!scoreAnalysis) {
    return (
      <aside className="hidden h-full items-center justify-center lg:flex lg:w-full lg:flex-col lg:gap-2 xl:gap-3 2xl:gap-4">
        <div className="text-gray-400">Loading scores...</div>
      </aside>
    );
  }

  // Define cards with real score data
  const cards = [
    {
      title: "Personal",
      icon: "personalDetails",
      score: scoreAnalysis.personalDetailsScore?.totalScore || 0,
      maxScore: Math.ceil(scoreAnalysis.personalDetailsMaxScore || 20),
      link: "/score-check/personal",
      requiresPremium: false,
    },
    {
      title: "Education",
      icon: "education",
      score: scoreAnalysis.educationScore?.totalScore || 0,
      maxScore: Math.ceil(scoreAnalysis.educationMaxScore || 20),
      link: "/score-check/education",
      requiresPremium: false,
    },
    {
      title: "Experience",
      icon: "experience",
      score: scoreAnalysis.experienceScore?.totalScore || 0,
      maxScore: Math.ceil(scoreAnalysis.experienceMaxScore || 20),
      link: "/score-check/experience",
      requiresPremium: true,
    },
    {
      title: "Projects",
      icon: "projects",
      score: scoreAnalysis.projectsScore?.totalScore || 0,
      maxScore: Math.ceil(scoreAnalysis.projectsMaxScore || 20),
      link: "/score-check/projects",
      requiresPremium: true,
    },
    {
      title: "Skills",
      icon: "personalDetails",
      score: scoreAnalysis.skillsScore?.totalScore || 0,
      maxScore: Math.ceil(scoreAnalysis.skillsMaxScore || 20),
      link: "/score-check/skills",
      requiresPremium: false,
    },
    {
      title: "Miscellaneous",
      icon: "overall",
      score:
        (scoreAnalysis.overAllScore || 0) +
        (scoreAnalysis.nonCoreSectionScore || 0),
      maxScore: Math.ceil(
        (scoreAnalysis.overallMaxScore || 0) +
          (scoreAnalysis.maxScores?.maxNonCoreSectionScore || 0)
      ),
      link: "/score-check/overall",
      requiresPremium: false,
    },
  ];

  return (
    <aside className="no-scrollbar hidden h-full overflow-y-auto lg:flex lg:w-full lg:flex-col lg:gap-2 xl:gap-2 2xl:gap-2 ">
      {/* Cards Grid - Responsive with tighter spacing */}
      <div className="grid grid-cols-2 gap-2 lg:gap-1.5 xl:gap-2.5 2xl:gap-3">
        {cards.map((card, idx) => (
          <SidebarSectionCard
            key={idx}
            title={card.title}
            icon={card.icon}
            score={card.score}
            maxScore={card.maxScore}
            detailsLink={card.link}
            isEssential={true}
            requiresPremium={card.requiresPremium}
          />
        ))}
      </div>

      {/* Recheck Resume Button - Same as ScoreNavbar */}
      <div className="mt-1 px-1 pb-2 lg:px-1.5 lg:pb-3 xl:px-2 xl:pb-3 2xl:px-2 2xl:pb-4">
        <button
          onClick={handleAnalyseResume}
          className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#C24AF9] via-[#F785F9] to-[#EF5AF0] py-2 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl lg:gap-2 lg:py-2.5 xl:gap-2.5 xl:py-3 2xl:gap-3 2xl:py-4"
        >
          {/* Shine animation overlay */}
          <div className="animate-shine absolute inset-0 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-1000 group-hover:translate-x-full"></div>

          {/* Button content */}
          <div className="relative flex items-center gap-2 lg:gap-2 xl:gap-2.5 2xl:gap-3">
            <Image
              src="/icons/checkScore.svg"
              alt="check score icon"
              width={20}
              height={20}
              className="h-4 w-4 lg:h-4 lg:w-4 xl:h-5 xl:w-5 2xl:h-5 2xl:w-5"
            />
            <span className="text-xs lg:text-sm xl:text-sm 2xl:text-base">
              Recheck score
            </span>
          </div>

          {/* Shine animation styles */}
          <style jsx>{`
            @keyframes shine {
              0% {
                transform: translateX(-100%) skewX(-12deg);
              }
              100% {
                transform: translateX(200%) skewX(-12deg);
              }
            }

            .animate-shine {
              animation: shine 2s infinite;
            }
          `}</style>
        </button>
      </div>

      {!isAuthLoading && !isPremiumUser && initialIsPremium !== "1" && (
        <div className="flex items-center justify-center">
          <Link href="/premium-subscription">
          <PremiumCard />
          </Link>
        </div>
      )}
    </aside>
  );
};

export default LeftSidebarCards;
